import { useState, useEffect, useCallback } from "react";
import { db } from "../../db";
import { Spinner, Empty, KpiStrip, Selo, BlockHead, ExpandTable, Chips } from "../../components/primitives";
import {
  getClienteHealth,
  canAdvanceTo,
  PIPELINE_STATUS_CFG,
  PIPELINE_STAGES,
  NEXT_STAGE,
} from "./fluxomedHealth";

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function fmtData(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const HEALTH_TONE  = { verde: "ok", amarelo: "warn", vermelho: "bad" };
const HEALTH_LABEL = { verde: "Saudável", amarelo: "Atenção", vermelho: "Agir" };

// Situação do pipeline → tom do selo do kit
export const STAGE_TONE = {
  rascunho: "off", homologacao: "warn", implantacao: "info",
  pronto_producao: "gold", producao: "ok", pausado: "bad",
};

// ─────────────────────────────────────────────────────────────
// PendenciasWidget — reutilizado aqui e no ClienteDetail
// ─────────────────────────────────────────────────────────────

export function PendenciasWidget({ cliente, implantacao }) {
  const { pendencias, podeLancamento, healthLevel, totalPendencias } =
    getClienteHealth(cliente, implantacao);

  if (totalPendencias === 0 && podeLancamento) {
    return (
      <div className="alert-line ok" style={{ marginBottom: 0 }}>
        <span aria-hidden="true">✓</span><span>Sem pendências: cliente pronto para produção</span>
      </div>
    );
  }

  const categorias = [
    { titulo: "Configuração", itens: pendencias.configuracao, tone: "warn" },
    { titulo: "Homologação",  itens: pendencias.homologacao,  tone: "info" },
    { titulo: "Implantação",  itens: pendencias.implantacao,  tone: "off"  },
  ].filter((c) => c.itens.length > 0);

  return (
    <details className="explain">
      <summary>
        <span className={`sev ${HEALTH_TONE[healthLevel] || ""}`} />
        <span>
          <strong>{totalPendencias} pendência{totalPendencias !== 1 ? "s" : ""}</strong>
          <span style={{ color: "var(--muted)" }}> · {categorias.map((c) => `${c.titulo} ${c.itens.length}`).join(" · ")}</span>
        </span>
      </summary>
      <div className="explain-body" style={{ display: "grid", gap: 10 }}>
        {categorias.map(({ titulo, itens, tone }) => (
          <div key={titulo} style={{ display: "grid", gap: 4 }}>
            <span><Selo tone={tone}>{titulo} · {itens.length}</Selo></span>
            {itens.map((item, i) => (
              <div key={i} style={{ fontSize: 13, color: "var(--ink)", paddingLeft: 4 }}>· {item}</div>
            ))}
          </div>
        ))}
      </div>
    </details>
  );
}

// ─────────────────────────────────────────────────────────────
// Botão de avanço do pipeline (mesmo gate de antes)
// ─────────────────────────────────────────────────────────────

function AvancarBtn({ cliente, implantacao, onAdvance, advancing }) {
  const nextStage = NEXT_STAGE[cliente.status];
  const nextCfg = nextStage ? PIPELINE_STATUS_CFG[nextStage] : null;
  if (!nextCfg) return null;
  const advance = canAdvanceTo(cliente, implantacao, nextStage);
  return (
    <button
      type="button"
      className={`btn btn-sm ${advance?.allowed ? "btn-primary" : "btn-secondary"}`}
      onClick={(e) => { e.stopPropagation(); if (advance?.allowed) onAdvance(cliente, nextStage); }}
      disabled={!advance?.allowed || advancing === cliente.id}
      title={advance?.allowed ? `Avançar para ${nextCfg.label}` : advance?.motivo}
    >
      {advancing === cliente.id ? "Avançando..." : advance?.allowed ? `→ ${nextCfg.label}` : `→ ${nextCfg.label} (bloqueado)`}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// FluxoMedCentral — componente principal (export)
// ─────────────────────────────────────────────────────────────

export function FluxoMedCentral({ onOpen }) {
  const [clientes,     setClientes]     = useState([]);
  const [implantacoes, setImplantacoes] = useState({});
  const [loading,      setLoading]      = useState(true);
  const [advancing,    setAdvancing]    = useState(null);
  const [etapa,        setEtapa]        = useState("todos");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clientesData, implData] = await Promise.all([
        db("fluxomed_clientes",    "GET", null, "?order=created_at.desc"),
        db("fluxomed_implantacao", "GET", null, "?order=created_at.desc"),
      ]);
      setClientes(clientesData || []);
      // Indexa implantações por cliente_id para lookup O(1)
      const idx = {};
      for (const impl of (implData || [])) {
        idx[impl.cliente_id] = impl;
      }
      setImplantacoes(idx);
    } catch (e) {
      console.error("[FluxoMed] Central: erro ao carregar:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAdvance(cliente, targetStage) {
    setAdvancing(cliente.id);
    try {
      await db("fluxomed_clientes", "PATCH",
        { status: targetStage, updated_at: new Date().toISOString() },
        `?id=eq.${cliente.id}`,
      );
      // Evento fire-and-forget
      db("fluxomed_historico", "POST", {
        cliente_id: cliente.id,
        evento:     "pipeline_avancado",
        descricao:  `Pipeline avançado: ${PIPELINE_STATUS_CFG[cliente.status]?.label} → ${PIPELINE_STATUS_CFG[targetStage]?.label}`,
        dados:      { de: cliente.status, para: targetStage },
      }).catch((e) => console.error("[FluxoMed] pipeline_avancado:", e));

      if (targetStage === "producao") {
        db("fluxomed_historico", "POST", {
          cliente_id: cliente.id,
          evento:     "cliente_producao",
          descricao:  "Cliente entrou em produção",
          dados:      { data: new Date().toISOString() },
        }).catch((e) => console.error("[FluxoMed] cliente_producao:", e));
      }

      setClientes((prev) =>
        prev.map((c) => c.id === cliente.id ? { ...c, status: targetStage } : c)
      );
    } catch (e) {
      console.error("[FluxoMed] Erro ao avançar pipeline:", e);
    } finally {
      setAdvancing(null);
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, gap: 12, color: "var(--muted)" }}>
        <Spinner size={22} />
        <span style={{ fontSize: 13 }}>Carregando Central Operacional...</span>
      </div>
    );
  }

  // KPIs
  const emProducao    = clientes.filter((c) => c.status === "producao").length;
  const emAndamento   = clientes.filter((c) => !["producao", "pausado"].includes(c.status || "rascunho")).length;
  const prontos       = clientes.filter((c) => c.status === "pronto_producao").length;
  const comProblemas  = clientes.filter((c) => {
    const h = getClienteHealth(c, implantacoes[c.id] || null);
    return h.healthLevel === "vermelho";
  }).length;

  const contagem = (st) => clientes.filter((c) => (c.status || "rascunho") === st).length;
  const filtro = PIPELINE_STAGES.includes(etapa) ? etapa : "todos";
  const linhas = filtro === "todos" ? clientes : clientes.filter((c) => (c.status || "rascunho") === filtro);

  return (
    <div className="view" style={{ paddingTop: 0 }}>
      <BlockHead
        title="Central operacional"
        sub={`Pipeline FluxoMed: ${clientes.length} cliente${clientes.length !== 1 ? "s" : ""}. Uma linha por cliente; abra para ver pendências.`}
      />

      <KpiStrip style={{ marginBottom: 16 }} items={[
        { label: "Em produção",   value: emProducao },
        { label: "Em andamento",  value: emAndamento },
        { label: "Prontos",       value: prontos, sub: prontos ? "podem entrar no ar" : undefined, tone: prontos ? "ok" : "" },
        { label: "Com problemas", value: comProblemas, sub: comProblemas ? "agir" : "nenhum", tone: comProblemas ? "bad" : "ok" },
      ]} />

      {clientes.length === 0 ? (
        <Empty icon="◎" text="Nenhum cliente FluxoMed cadastrado ainda." />
      ) : (
        <>
          <div className="tab-bar">
            <Chips
              label="Filtrar por etapa"
              active={filtro}
              onChange={setEtapa}
              items={[
                { id: "todos", label: "Todos", count: clientes.length },
                ...PIPELINE_STAGES.map((st) => ({ id: st, label: PIPELINE_STATUS_CFG[st].label, count: contagem(st) })),
              ]}
            />
          </div>
          <ExpandTable
            minWidth={720}
            rows={linhas}
            empty="Nenhum cliente nesta etapa"
            columns={[
              { key: "nome", label: "Cliente", render: (c) => (
                <div><div className="name">{c.nome}</div>{c.medico_nome && <div className="sub">{c.medico_nome}</div>}</div>
              ) },
              { key: "etapa", label: "Etapa", render: (c) => {
                const st = c.status || "rascunho";
                return <Selo tone={STAGE_TONE[st] || "off"}>{PIPELINE_STATUS_CFG[st]?.label || st}</Selo>;
              } },
              { key: "saude", label: "Saúde", render: (c) => {
                const h = getClienteHealth(c, implantacoes[c.id] || null);
                return (
                  <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <Selo tone={HEALTH_TONE[h.healthLevel] || "off"}>{HEALTH_LABEL[h.healthLevel] || h.healthLevel}</Selo>
                    <span className="sub">{h.totalPendencias ? `${h.totalPendencias} pend.` : "sem pendências"}</span>
                  </span>
                );
              } },
              { key: "desde", label: "Desde", render: (c) => <span className="sub">{fmtData(c.created_at) || "—"}</span> },
              { key: "acao", label: "", align: "right", render: (c) => (
                <AvancarBtn cliente={c} implantacao={implantacoes[c.id] || null} onAdvance={handleAdvance} advancing={advancing} />
              ) },
            ]}
            detail={(c) => (
              <div className="detail-grid">
                <PendenciasWidget cliente={c} implantacao={implantacoes[c.id] || null} />
                <div className="detail-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => onOpen(c.id)}>Abrir ficha</button>
                </div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
